import React, { useState, useEffect } from 'react';
import { Users, RefreshCw, Trash2, AlertCircle, CheckCircle, XCircle, Info, Settings as SettingsIcon } from 'lucide-react';
import contactSyncService from '../services/ContactSyncService';
import ContactPermissionDialog from './ContactPermissionDialog';

/**
 * ContactSyncSettings - User consent toggle and contact sync management
 * Shows in Settings page or Mobile Settings
 */
const ContactSyncSettings = ({ userId, appId }) => {
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [syncStats, setSyncStats] = useState({ total: 0, lastSync: null });

  useEffect(() => {
    loadSyncSettings();
  }, [userId, appId]);

  const loadSyncSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get sync status
      const status = await contactSyncService.getSyncStatus();
      setSyncStatus(status);

      // Check if sync is enabled (stored in preferences/localStorage)
      const enabled = localStorage.getItem(`acctoo_contact_sync_enabled_${userId}`);
      setSyncEnabled(enabled === 'true');

      const autoSync = localStorage.getItem(`acctoo_contact_auto_sync_${userId}`);
      setAutoSyncEnabled(autoSync === 'true');

      // Get synced contacts count
      if (enabled === 'true') {
        const { success, contacts } = await contactSyncService.getSyncedContacts(appId, userId);
        if (success) {
          setSyncStats({
            total: contacts.length,
            lastSync: status.lastSyncDate
          });
        }
      }
    } catch (error) {
      console.error('Error loading sync settings:', error);
      setError('Failed to load sync settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableSync = async () => {
    // Show permission dialog first
    setShowPermissionDialog(true);
  };

  const handlePermissionAccept = async () => {
    setShowPermissionDialog(false);
    setError(null);
    setSuccess(null);
    setSyncing(true);

    try {
      // Check permission
      const permission = await contactSyncService.checkPermission();
      
      if (!permission.granted) {
        // Request permission
        const requestResult = await contactSyncService.requestPermission();
        if (!requestResult.granted) {
          setError('Contacts permission is required to enable sync. Please grant permission in your device settings.');
          setSyncing(false);
          return;
        }
      }

      // Enable sync
      localStorage.setItem(`acctoo_contact_sync_enabled_${userId}`, 'true');
      setSyncEnabled(true);

      // Perform initial sync
      const syncResult = await contactSyncService.syncContactsToDatabase(userId, appId);
      
      if (syncResult.success) {
        setSuccess(`Successfully synced ${syncResult.syncedCount} contacts!`);
        await loadSyncSettings(); // Reload stats
      } else {
        setError(syncResult.message || 'Failed to sync contacts');
        // Revert enable if sync failed
        localStorage.removeItem(`acctoo_contact_sync_enabled_${userId}`);
        setSyncEnabled(false);
      }
    } catch (error) {
      console.error('Error enabling sync:', error);
      setError(`Failed to enable contact sync: ${error.message}`);
      localStorage.removeItem(`acctoo_contact_sync_enabled_${userId}`);
      setSyncEnabled(false);
    } finally {
      setSyncing(false);
    }
  };

  const handlePermissionDecline = () => {
    setShowPermissionDialog(false);
  };

  const handleDisableSync = async () => {
    if (!window.confirm('Are you sure you want to disable contact sync? Synced contacts will remain in your account.')) {
      return;
    }

    try {
      localStorage.removeItem(`acctoo_contact_sync_enabled_${userId}`);
      setSyncEnabled(false);
      setSuccess('Contact sync disabled');
      await loadSyncSettings();
    } catch (error) {
      console.error('Error disabling sync:', error);
      setError('Failed to disable contact sync');
    }
  };

  const handleManualSync = async () => {
    if (!syncEnabled) {
      setError('Please enable contact sync first');
      return;
    }

    setError(null);
    setSuccess(null);
    setSyncing(true);

    try {
      const result = await contactSyncService.syncContactsToDatabase(userId, appId);
      
      if (result.success) {
        setSuccess(`Successfully synced ${result.syncedCount} contacts!`);
        await loadSyncSettings(); // Reload stats
      } else {
        setError(result.message || 'Failed to sync contacts');
      }
    } catch (error) {
      console.error('Error syncing contacts:', error);
      setError(`Failed to sync contacts: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleClearContacts = async () => {
    if (!window.confirm('Are you sure you want to clear all synced contacts? This will remove them from AccToo but not from your device.')) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      
      const result = await contactSyncService.clearSyncedContacts(appId, userId);
      
      if (result.success) {
        setSuccess('Synced contacts cleared successfully');
        await loadSyncSettings();
      } else {
        setError(result.message || 'Failed to clear contacts');
      }
    } catch (error) {
      console.error('Error clearing contacts:', error);
      setError('Failed to clear contacts');
    }
  };

  const handleToggleAutoSync = () => {
    const newValue = !autoSyncEnabled;
    setAutoSyncEnabled(newValue);
    localStorage.setItem(`acctoo_contact_auto_sync_${userId}`, newValue.toString());
    setSuccess(newValue ? 'Auto-sync enabled' : 'Auto-sync disabled');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Contact Sync</h2>
                <p className="text-sm text-gray-600">
                  {syncEnabled ? 'Sync is active' : 'Sync is disabled'}
                </p>
              </div>
            </div>
            
            {/* Main toggle */}
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-700 font-medium">
                {syncEnabled ? 'Enabled' : 'Disabled'}
              </span>
              <button
                onClick={syncEnabled ? handleDisableSync : handleEnableSync}
                disabled={syncing}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  syncEnabled ? 'bg-blue-600' : 'bg-gray-300'
                } ${syncing ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    syncEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-800">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="text-green-600 hover:text-green-800"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Info Banner */}
        <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">What does this do?</p>
              <p>
                Contact sync imports your phone contacts to AccToo, making it easier to add 
                customers and suppliers. We only sync name, phone, and email - nothing else.
              </p>
            </div>
          </div>
        </div>

        {/* Stats (when enabled) */}
        {syncEnabled && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Synced Contacts</p>
                <p className="text-2xl font-bold text-gray-900">{syncStats.total}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Last Sync</p>
                <p className="text-sm font-medium text-gray-900">
                  {syncStats.lastSync || 'Never'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Settings (when enabled) */}
        {syncEnabled && (
          <div className="px-6 py-4 border-t border-gray-200 space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center">
              <SettingsIcon className="w-5 h-5 mr-2" />
              Sync Settings
            </h3>

            {/* Auto-sync toggle */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-900">Auto-sync</p>
                <p className="text-sm text-gray-600">
                  Automatically sync contacts periodically
                </p>
              </div>
              <button
                onClick={handleToggleAutoSync}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  autoSyncEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoSyncEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Manual sync button */}
            <button
              onClick={handleManualSync}
              disabled={syncing}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
              <span className="font-medium">
                {syncing ? 'Syncing...' : 'Sync Now'}
              </span>
            </button>

            {/* Clear contacts button */}
            <button
              onClick={handleClearContacts}
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              <Trash2 className="w-5 h-5" />
              <span className="font-medium">Clear Synced Contacts</span>
            </button>
          </div>
        )}

        {/* Privacy note */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-start space-x-2 text-xs text-gray-600">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              Your contacts are stored securely and never shared with third parties. 
              You can disable sync and delete all synced data at any time. 
              Read our{' '}
              <a 
                href="/privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Privacy Policy
              </a>
              {' '}for more information.
            </p>
          </div>
        </div>
      </div>

      {/* Permission Dialog */}
      <ContactPermissionDialog
        isOpen={showPermissionDialog}
        onAccept={handlePermissionAccept}
        onDecline={handlePermissionDecline}
        mode="request"
      />
    </>
  );
};

export default ContactSyncSettings;

























































